import { useEffect, useState } from "react";
import { Alert, Form, Input, Modal, notification } from "antd";

import { ApiError, type PaymentMethodRow, api } from "../api/client";
import { ManagedListTable } from "../components/settings/ManagedListTable";
import { systemRowLabel } from "../components/settings/systemRow";

interface FormValues {
  name: string;
}

/**
 * Settings page — manage settlement payment methods (Cash, Card, etc.).
 *
 * Uses the shared ``<ManagedListTable>`` for DnD reorder, archive/restore
 * and inline edit. The page owns the create/edit modal.
 *
 * The four canonical methods (Cash / Card / Invoice / Corporate Pays) are
 * "system" rows — ``system_seeded`` is computed server-side from their
 * fixed ``code`` (see ``SYSTEM_PAYMENT_METHOD_CODES``). They're renameable
 * like any method, but their Archive button is disabled and the server
 * 403s deactivate/delete (code-side lookups pin to the code). Once renamed
 * away from its canonical name a system row shows the fixed code in
 * brackets, e.g. "Kasse (cash)".
 */
export const PaymentMethodsPage = () => {
  const [rows, setRows] = useState<PaymentMethodRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentMethodRow | null>(null);
  const [form] = Form.useForm<FormValues>();

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      // ``include_inactive=true`` so archived rows render in the
      // "Archived" collapse section.
      setRows(await api.listPaymentMethods(true));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ name: "" });
    setModalOpen(true);
  };

  const openEdit = (row: PaymentMethodRow) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({ name: row.name });
    setModalOpen(true);
  };

  const onSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        if (values.name.trim() !== editing.name) {
          await api.updatePaymentMethod(editing.id, { name: values.name.trim() });
        }
      } else {
        await api.createPaymentMethod({
          name: values.name.trim(),
          sort_order: rows.length * 10,
        });
      }
      setModalOpen(false);
      setEditing(null);
      await refresh();
    } catch (err) {
      notification.error({
        message: editing ? "Update failed" : "Create failed",
        description: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const onArchive = async (row: PaymentMethodRow) => {
    setBusy(true);
    try {
      await api.deletePaymentMethod(row.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async (row: PaymentMethodRow) => {
    setBusy(true);
    try {
      await api.updatePaymentMethod(row.id, { active: true });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onReorder = async (orderedIds: number[]) => {
    try {
      await api.reorderPaymentMethods(orderedIds);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      await refresh();
    }
  };

  return (
    <>
      <ManagedListTable<PaymentMethodRow>
        title="Payment methods"
        description="Used by the settlement modal to record how a balance was cleared."
        rows={rows}
        loading={loading}
        busy={busy}
        error={error}
        onErrorDismiss={() => setError(null)}
        addLabel="+ New payment method"
        // Surface the fixed code in brackets once a canonical method is
        // renamed away from its origin (e.g. "Kasse (cash)").
        labelOf={systemRowLabel}
        keyOf={(r) => r.id}
        isArchived={(r) => !r.active}
        isSystem={(r) => r.system_seeded}
        systemArchiveTooltip="Canonical methods (Cash, Card, Invoice, Corporate Pays) back code-side lookups and can't be archived."
        onReorder={onReorder}
        onCreate={openCreate}
        onEdit={openEdit}
        onArchive={onArchive}
        onRestore={onRestore}
      />
      <Modal
        title={editing ? "Edit payment method" : "New payment method"}
        open={modalOpen}
        onOk={() => void onSubmit()}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        okText={editing ? "Save" : "Create"}
        confirmLoading={busy}
        destroyOnHidden
      >
        {editing?.system_seeded && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`System payment method — renaming only changes the display label; its identity code "${editing.code}" stays fixed.`}
          />
        )}
        <Form<FormValues> form={form} layout="vertical">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input maxLength={64} autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
